import { useState } from "react";
import Modal from "../shared/Modal";
import { useSelector, useDispatch } from "react-redux";
import {
  apiCreateWorkspaceForUser,
  apiGetWorkspace,
  apiSetCurrentWorkspace,
} from "../../api.js";
import {
  selectUser,
  setUser,
  setLinkRecords,
} from "../../redux/features/appData";
import { TRACK_EVENTS, trackAnalyticsEvent } from "../../hooks/useAnalytics";
import "./ModalAddWorkspace.css";
import { useCoreDataLoader } from "../../hooks/useCoreDataLoader";
import { apiCreateCollectionWithEverything } from "../../services/CollectionService";
import {
  CollectionAccessTypes,
  CreateCollectionWithEverythingContract,
} from "winslow-core";
import { toastFail } from "../../config";
import toast from "react-hot-toast";

type ModalAddWorkspaceProps = {
  isOpen: boolean;
  onClose: any;
  onSuccess: any;
};

export default function ModalAddWorkspace({
  isOpen = false,
  onClose,
  onSuccess,
}: ModalAddWorkspaceProps) {
  const dispatch = useDispatch();
  const { reloadAll } = useCoreDataLoader();
  const [newWorkspaceName, setNewWorkspaceName] = useState("");
  const [newWorkspaceError, setNewWorkspaceError] = useState("");

  const storeUser = useSelector(selectUser);

  const saveNewWorkspace = async () => {
    if (!storeUser) {
      return;
    }

    const workspace = await apiCreateWorkspaceForUser(
      newWorkspaceName,
      storeUser.id
    );

    if (workspace) {
      // now create the default collection
      const payload = {
        title: "title for workspace " + workspace.id,
        description: "",
        color: "#B9B9B9",
        userId: storeUser?.id,
        workspaceId: workspace.id,
        accessType: CollectionAccessTypes.TEAM,
      } as CreateCollectionWithEverythingContract["Body"];

      const collection = await apiCreateCollectionWithEverything(payload);

      if (!collection) {
        toast("Problem creating collection for workspace.", toastFail);
        return;
      }

      const updatedWorkspace = await apiGetWorkspace(workspace.id);

      trackAnalyticsEvent(TRACK_EVENTS.CreateWorkspace, {}, storeUser);

      // add the new one to the current store user
      const newWorkspaces = [...storeUser.workspaces, updatedWorkspace];
      const newUser = {
        ...storeUser,
        workspaces: newWorkspaces,
        currentWorkspaceId: updatedWorkspace.id,
      };

      dispatch(setUser(newUser));
      dispatch(setLinkRecords(null));
      reloadAll(updatedWorkspace.id);
      await apiSetCurrentWorkspace(storeUser.id, workspace.id);
    }

    onSuccess();
  };

  if (!isOpen) {
    return null;
  }

  return (
    <>
      <Modal
        onClose={onClose}
        style={{}}
        isOpen={isOpen}
        canClose={false}
        allowClickout={true}>
        <div className="add-workspace-container">
          Create A New Workspace
          <div className="add-workspace-input">
            <input
              type="text"
              autoFocus={true}
              placeholder="Name your workspace"
              value={newWorkspaceName}
              onChange={(e) => {
                setNewWorkspaceName(e.target.value);
              }}
              onKeyDown={(e: any) => {
                if (e.key === "Enter" && newWorkspaceName.length > 0) {
                  saveNewWorkspace();
                }
              }}
            />
            {newWorkspaceError && (
              <div style={{ color: "var(--text-color-error)" }}>
                {newWorkspaceError}
              </div>
            )}
          </div>
          <div>
            You can change your name and customize your workspace logo at
            anytime in your workspace settings.
          </div>
          <div className="add-workspace-buttons">
            <button
              type="button"
              onClick={onClose}
              className="add-workspace-buttons-cancel">
              Cancel
            </button>
            <button
              type="button"
              disabled={newWorkspaceName?.length < 1}
              onClick={saveNewWorkspace}
              className="add-workspace-buttons-add">
              Create
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
}
